(function () {
  const SECTION_CLASS = "image-tabs-section";

  const AUTOPLAY_DELAY = 5000;

  // One index drives the whole section: the tab list, the media slides and the
  // description roll. The copy always rolls the one way, in from below and up out
  // of the frame, whichever tab is taken — keyed to the travel direction it ran
  // backwards for a tab above the current one, which reads as the copy changing
  // its mind rather than as one hand-over.
  const createTabs = (section) => {
    const tabs = Array.from(
      section.querySelectorAll("[data-images-tabs-item]")
    );
    const slides = Array.from(
      section.querySelectorAll("[data-image-tabs-slide]")
    );
    const groups = Array.from(
      section.querySelectorAll("[data-image-tabs-descriptions]")
    );
    if (tabs.length === 0 || slides.length === 0) return null;

    const indexOf = (el) => Number(el.dataset?.imageTabsIndex || 0);
    const slots = () =>
      groups.flatMap((group) =>
        Array.from(group.querySelectorAll("[data-image-tabs-description]"))
      );

    const currentIndex = () => {
      const active = tabs.find((tab) => tab.classList.contains("active"));
      return active ? indexOf(active) : 0;
    };

    // Where a slot stands right now, in % of its own box — mid-transition included.
    const offsetOf = (el) => {
      const matrix = new DOMMatrixReadOnly(getComputedStyle(el).transform);
      return (matrix.m42 / (el.offsetHeight || 1)) * 100;
    };

    const sendTo = (el, offset) => {
      el.style.transform = "translateY(" + offset + "%)";
    };

    // Every copy shares one box, so two of them closer than a full box put two texts
    // on the same line. Left to CSS, a switch that cuts an earlier roll short does
    // exactly that: the copy still travelling runs on its own clock and the fresh
    // pair catches up with it. So the whole stack is driven by hand — the incoming
    // copy is parked a full box beyond the outermost copy still inside the frame, and
    // every one of them then travels THAT distance, in the same time. Moving as one
    // piece, the stack keeps whatever spacing it had. Copies already off-frame are
    // left alone; they are invisible until they come back as the incoming one.
    const planRoll = (group, index) => {
      const copies = Array.from(
        group.querySelectorAll("[data-image-tabs-description]")
      );
      const incoming = copies.filter((el) => indexOf(el) === index);
      const moving = [];
      let edge = 0;

      // The group that belongs to the other breakpoint is not rendered, so it has no
      // geometry to measure — hand it back to the class-driven states, which is also
      // what it needs to be in when a resize brings it into view.
      if (group.offsetHeight === 0) {
        copies.forEach((el) => {
          el.style.transform = "";
        });
        return null;
      }

      copies.forEach((el) => {
        if (incoming.indexOf(el) !== -1) return;
        const offset = offsetOf(el);
        // The stack is whatever stands inside the frame plus the copy handing over,
        // which spends most of its roll off-frame on the way IN. Everything else is
        // out of the frame heading further out, or parked there, and never comes back
        // on its own — dragging it along would pull it into view backwards.
        if (Math.abs(offset) > 99.5 && !el.classList.contains("active")) return;
        moving.push({ el: el, offset: offset });
        if (offset > edge) edge = offset;
      });

      // A box below the lowest copy still in play, so the stack always travels up.
      const start = edge + 100;

      // Parked while the copy carries no transition, so the reflow below commits it.
      incoming.forEach((el) => sendTo(el, start));

      return { incoming: incoming, moving: moving, start: start };
    };

    const applyRoll = (roll) => {
      if (!roll) return;
      roll.incoming.forEach((el) => sendTo(el, 0));
      roll.moving.forEach((item) => sendTo(item.el, item.offset - roll.start));
    };

    // byHand marks a tab taken by pointer or keyboard, the only case whose rule
    // mirrors: autoplay always travels down the list — including the wrap back to the
    // first tab, which as a mirrored step would flip the sweep on every lap.
    const activate = (index, byHand) => {
      if (index < 0 || index >= tabs.length) return;
      const from = currentIndex();
      // Re-activating the current tab is a no-op — hovering it must not replay anything.
      if (from === index) return;

      // Only the rule follows the way the list is travelled; the copy always rolls in
      // from below (planRoll), whichever tab is taken.
      const back = byHand === true && index < from;
      const enteringTab = tabs.find((tab) => indexOf(tab) === index);
      const leavingTab = tabs.find((tab) => indexOf(tab) === from);

      const rolling = [...tabs, ...slots()];
      const entering = rolling.filter((el) => indexOf(el) === index);
      const leaving = rolling.filter(
        (el) => el.classList.contains("active") && indexOf(el) !== index
      );

      // The entering elements are parked off-frame with no transition, so one reflow
      // lands them on the side they have to travel from. Without it an element still
      // animating out would simply reverse from wherever it stands. The roll is
      // planned in the same breath: it reads where the copies stand BEFORE any class
      // moves them.
      entering.forEach((el) => el.classList.remove("is-leaving", "active"));
      // The side the rule comes in from is part of what that reflow commits.
      if (enteringTab) enteringTab.classList.toggle("is-reversed", back);
      const rolls = groups.map((group) => planRoll(group, index));
      void section.offsetWidth;

      // The one handed over leaves by the side this move goes to, whichever side it
      // came in on, so the two travel together.
      if (leavingTab) leavingTab.classList.toggle("is-reversed", back);
      leaving.forEach((el) => {
        el.classList.remove("active");
        el.classList.add("is-leaving");
      });
      entering.forEach((el) => el.classList.add("active"));
      rolls.forEach(applyRoll);

      // Each tab carries role=button, so the state it announces is aria-pressed.
      tabs.forEach((tab) => {
        const control = tab.querySelector(".image-tabs__item-link");
        if (!control) return;
        control.setAttribute("aria-pressed", String(indexOf(tab) === index));
      });

      slides.forEach((slide) => {
        if (indexOf(slide) !== index) {
          slide.classList.remove("active");
          return;
        }
        slide.classList.add("active");
        // Mask reveal on switch only — activate() isn't called on initial load, so
        // the default tab stays static. ONLY the incoming slide is reset: a reveal
        // cut short by the next tab keeps opening beneath the new one until it
        // fades, where clearing the class would show the whole frame in a single
        // frame (a flash when a pointer sweeps the tab list). Remove + reflow +
        // add — a same-frame re-add never restarts a keyframe.
        slide.classList.remove("is-revealing");
        void slide.offsetWidth;
        slide.classList.add("is-revealing");
      });
    };

    return { activate, currentIndex, count: tabs.length, tabs };
  };

  const initDesktopToggle = (section, controller, api) => {
    // Auto-switch while idle; pauses on interaction, resumes on leave. The delay
    // comes from the section setting, with the constant covering a section instance
    // saved before the setting existed.
    const list = section.querySelector("[data-image-tabs-list]");
    const delay = Number(list?.dataset.autoplayDelay) || AUTOPLAY_DELAY;
    let timer = null;
    const stopAuto = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const startAuto = () => {
      stopAuto();
      if (window.innerWidth < 750 || api.count < 2) return;
      timer = setInterval(() => {
        api.activate((api.currentIndex() + 1) % api.count);
      }, delay);
    };

    api.tabs.forEach((tab) => {
      const idx = Number(tab.dataset?.imageTabsIndex || 0);
      // Hover switches on the frame the pointer lands, with nothing held back: an
      // intent delay kept the roll clean while a pointer crossed the tabs on its
      // way, but the tab actually aimed for then answered late, which reads as the
      // section lagging. A roll cut short by the next tab is planned from where
      // the copies stand (planRoll), so a fast sweep still keeps its spacing.
      const go = () => {
        if (window.innerWidth < 750) return;
        stopAuto();
        api.activate(idx, true);
      };
      tab.addEventListener("mouseover", go, { signal: controller.signal });
      tab.addEventListener("focusin", go, { signal: controller.signal });
      // Focus already switches the tab, but a role=button has to answer Enter and
      // Space as well — and Space must not scroll the page.
      tab.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          if (!event.target.closest('[role="button"]')) return;
          event.preventDefault();
          go();
        },
        { signal: controller.signal }
      );
    });

    section.addEventListener("mouseleave", startAuto, {
      signal: controller.signal,
    });

    startAuto();

    return { stopAuto, startAuto };
  };

  const initSlider = (section, api) => {
    const sliderEl = section.querySelector(".js-image-tabs-slider");
    if (!sliderEl) return;

    const wrapperEl = sliderEl.querySelector(".image-tabs__media-wrapper");
    const slidesEls = sliderEl.querySelectorAll("[data-image-tabs-slide]");

    sliderEl.classList.add("swiper");
    wrapperEl.classList.add("swiper-wrapper");

    slidesEls.forEach((slide) => {
      slide.classList.add("swiper-slide");
    });

    const sliderLoop = sliderEl.getAttribute("data-loop") === "true";
    const hasPagination = sliderEl.getAttribute("data-pagination") === "true";
    const scrollbarEl = section.querySelector(".slider-scrollbar");
    const hasSliderAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
    const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 8000);

    new Swiper(sliderEl, {
      loop: sliderLoop,
      slidesPerView: 1,
      spaceBetween: 12,
      speed: 800,
      watchSlideProgress: true,
      allowTouchMove: true,
      mousewheel: {
        forceToAxis: true,
      },
      a11y: {
        scrollOnFocus: false,
      },
      autoplay: hasSliderAutoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
          }
        : false,
      // Shared draggable scrollbar, as on the card sliders.
      scrollbar:
        hasPagination && scrollbarEl
          ? {
              el: scrollbarEl,
              draggable: true,
              grabCursor: true,
              hide: false,
            }
          : false,
      on: {
        slideChange(swiper) {
          // The description sits outside the slider, so swiping has to roll it too.
          if (api) api.activate(swiper.realIndex);
        },
      },
    });
  };

  const destroySlider = (section) => {
    const sliderEl = section.querySelector(".js-image-tabs-slider");
    if (!sliderEl || !sliderEl.swiper) return;

    const wrapperEl = sliderEl.swiper.wrapperEl;
    const slidesEls = sliderEl.swiper.slides || [];

    if (wrapperEl) {
      wrapperEl.removeAttribute("style");
      wrapperEl.classList.remove("swiper-wrapper");
    }

    slidesEls.forEach((slide) => {
      slide.removeAttribute("style");
      slide.classList.remove("swiper-slide");
    });

    // cleanStyles: true so Swiper also takes its own classes back off the
    // container. .swiper carries margin-inline: auto, which otherwise survives the
    // teardown and centres the media in the desktop row, eating the gutter after a
    // resize up from mobile — and the leftover .swiper-initialized would keep the
    // pre-init fallback off.
    sliderEl.swiper.destroy(true, true);
    [...sliderEl.classList]
      .filter((name) => name.startsWith("swiper"))
      .forEach((name) => sliderEl.classList.remove(name));
    sliderEl.swiper = null;
  };

  const initSection = (section) => {
    if (!section || !section.classList.contains(SECTION_CLASS)) return;

    if (section._imageTabsCleanup) {
      section._imageTabsCleanup();
      section._imageTabsCleanup = null;
    }

    const controller = new AbortController();
    const api = createTabs(section);
    const auto = api ? initDesktopToggle(section, controller, api) : null;
    if (api && auto) section._imageTabsEditor = { api, auto };

    let resizeObserver = null;
    const sliderEl = section.querySelector(".js-image-tabs-slider");
    if (sliderEl) {
      resizeObserver = new ResizeObserver(() => {
        if (window.innerWidth < 750 && !sliderEl.swiper) {
          initSlider(section, api);
        } else if (window.innerWidth >= 750 && sliderEl.swiper) {
          destroySlider(section);
        }
      });
      resizeObserver.observe(section);
    }

    section._imageTabsCleanup = () => {
      controller.abort();
      if (auto) auto.stopAuto();
      if (resizeObserver) resizeObserver.disconnect();
      destroySlider(section);
      section._imageTabsEditor = null;
    };
  };

  initSection(document.currentScript.parentElement);

  if (!window.__themeImageTabsEvents) {
    window.__themeImageTabsEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const section = event.target;
      if (section && section._imageTabsCleanup) {
        section._imageTabsCleanup();
        section._imageTabsCleanup = null;
      }
    });

    // Theme editor: show the tab of the block being edited and hold it there —
    // autoplay would otherwise cycle away from the setting being typed into.
    document.addEventListener("shopify:block:select", (event) => {
      const target = event.target;
      const item = target.closest?.(
        "[data-images-tabs-item], [data-image-tabs-slide]"
      );
      const editor = target.closest?.("." + SECTION_CLASS)?._imageTabsEditor;
      if (!item || !editor) return;
      editor.auto.stopAuto();
      editor.api.activate(Number(item.dataset?.imageTabsIndex || 0));
    });

    document.addEventListener("shopify:block:deselect", (event) => {
      const editor = event.target.closest?.(
        "." + SECTION_CLASS
      )?._imageTabsEditor;
      if (editor) editor.auto.startAuto();
    });
  }
})();
